import React, { useState, useEffect } from "react";
import Select from "react-select";
import { api } from "../shared/axiosInstance";
import { useCallback } from "react";

function formatOrdinal(n) {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const Matches = () => {
    const [selectedTab, setSelectedTab] = useState("jobs");
    const [topMatches, setTopMatches] = useState([]);
    const [referredUsers, setReferredUsers] = useState([]);
    const [jobMatches, setJobMatches] = useState([]);
    const [jobStatusFilter, setJobStatusFilter] = useState("all");
    const [loadingTop, setLoadingTop] = useState(true);
    const [loadingReferred, setLoadingReferred] = useState(true);
    const [loadingJobMatches, setLoadingJobMatches] = useState(true);
    const [isMatched, setIsMatched] = useState(null); // null = unknown, true = matched, false = processing
    const [checkingStatus, setCheckingStatus] = useState(true);

    const impersonatedUserId = localStorage.getItem("impersonatedUserId");
    const token = localStorage.getItem("token");

    useEffect(() => {
        let intervalId = null;
        let cancelled = false;

        const checkMatchStatus = async () => {
            try {
                const response = await api.get("/matches/match-status", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (cancelled) return;

                const { isMatched: matched, allQuestionsAnswered } = response.data;

                if (matched) {
                    setIsMatched(true);
                    setCheckingStatus(false);
                    // Clear polling interval since matching is done
                    if (intervalId) {
                        clearInterval(intervalId);
                        intervalId = null;
                    }
                } else if (allQuestionsAnswered) {
                    // Assessment done but matching still processing
                    setIsMatched(false);
                    setCheckingStatus(false);
                } else {
                    // Assessment not complete — no polling needed, just show matches tab normally
                    setIsMatched(true); // treat as "ready" — they just won't have matches yet
                    setCheckingStatus(false);
                }
            } catch (err) {
                console.error("Error checking match status:", err);
                if (!cancelled) {
                    setCheckingStatus(false);
                    setIsMatched(true); // On error, fall through to normal view
                }
            }
        };

        checkMatchStatus();

        // Poll every 10 seconds only if we don't know status yet or status is "processing"
        intervalId = setInterval(() => {
            checkMatchStatus();
        }, 10000);

        return () => {
            cancelled = true;
            if (intervalId) clearInterval(intervalId);
        };
    }, [token]);

    let currentUser = null;
    if (impersonatedUserId) {
        currentUser = { userId: impersonatedUserId };
    } else {
        try {
            const decoded = token ? JSON.parse(atob(token.split('.')[1])) : null;
            currentUser = decoded ? { userId: decoded.userId } : null;
        } catch (err) {
            console.error("Failed to decode token", err);
        }
    }

    const fetchJobMatches = useCallback(async () => {
        try {
            const response = await api.get("/matches/job", {
                params: { jobStatus: jobStatusFilter, userId: currentUser.userId },
                headers: { Authorization: `Bearer ${token}` },
            });
            setJobMatches(response.data.jobs || []);
        } catch (err) {
            console.error("Error fetching job matches:", err);
        } finally {
            setLoadingJobMatches(false);
        }
    }, [jobStatusFilter, currentUser.userId, token]);

    useEffect(() => {
        if (selectedTab === "jobs") {
            setLoadingJobMatches(true);
            fetchJobMatches();
        }
    }, [selectedTab, jobStatusFilter, fetchJobMatches]);

    useEffect(() => {
        if (selectedTab === "people") {
            api.get(`/matches/person/top?userId=${encodeURIComponent(currentUser.userId)}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
                .then((res) => {
                    const matches = res.data.matches || [];
                    matches.sort((a, b) => b.similarityScore - a.similarityScore);
                    setTopMatches(matches.slice(0, 10));
                    setLoadingTop(false);
                })
                .catch((err) => {
                    console.error("Error fetching top matches:", err);
                    setTopMatches([]);
                    setLoadingTop(false);
                });

            api.get(`/matches/person/referred?userId=${encodeURIComponent(currentUser.userId)}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
                .then((res) => {
                    setReferredUsers(res.data.referredUsers);
                    setLoadingReferred(false);
                })
                .catch((err) => {
                    console.error("Error fetching referred users:", err);
                    setLoadingReferred(false);
                });
        }

    }, [selectedTab, token, currentUser.userId]);

    const handleApply = async (jobId, isAlreadyApplied) => {
        try {
            const confirmMessage = isAlreadyApplied
                ? "Are you sure you want to remove your application?"
                : "Confirm you have applied for this job?";

            if (!window.confirm(confirmMessage)) {
                return; // User cancelled
            }

            if (isAlreadyApplied) {
                await api.post("/matches/unapply", { jobId }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                await api.post("/matches/apply", { jobId }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            fetchJobMatches(); // Refresh list
        } catch (err) {
            console.error("❌ Error updating application status:", err);
        }
    };

    // const formatSimilarity = (score) => {
    //     if (typeof score !== "number" || isNaN(score)) {
    //         return "";
    //     }
    //     return `${(score * 100).toFixed(2)}%`;
    // };

    const statusOptions = [
        { value: "all", label: "All" },
        { value: "open", label: "Open Only" },
        { value: "filled", label: "Filled Only" }
    ];

    const selectStyles = {
        control: (provided) => ({
            ...provided,
            backgroundColor: 'var(--color-deepblue)',
            borderColor: 'var(--color-lightblue)',
            color: 'var(--color-lightgray)',
            minHeight: '38px'
        }),
        input: (provided) => ({
            ...provided,
            color: 'var(--color-lightgray)'
        }),
        placeholder: (provided) => ({
            ...provided,
            color: 'var(--color-lightgray)'
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: 'var(--color-bluegray)',
            color: 'var(--color-lightgray)'
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? 'var(--color-orange)' : 'var(--color-bluegray)',
            color: state.isFocused ? '#fff' : 'var(--color-lightgray)',
            cursor: 'pointer'
        }),
        singleValue: (provided) => ({
            ...provided,
            color: 'var(--color-lightgray)'
        })
    };

    return (
        <div className="px-6 py-8 bg-darkblue text-lightgray">
            <h1 className="text-3xl font-bold text-center text-orange mb-6">Matches</h1>

            <div className="flex border-b border-lightblue mb-8 justify-center">
                <button
                    className={`px-4 py-2 ml-4 ${selectedTab === "jobs" ? "border-b-2 border-orange text-orange" : "text-gray-400"}`}
                    onClick={() => setSelectedTab("jobs")}
                >
                    Jobs
                </button>
                <button
                    className={`px-4 py-2 ${selectedTab === "people" ? "border-b-2 border-orange text-orange" : "text-gray-400"}`}
                    onClick={() => setSelectedTab("people")}
                >
                    People
                </button>
                </div>

            {(checkingStatus || isMatched === false) && (
                <div className="bg-bluegray p-8 rounded-lg shadow-md text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange mx-auto mb-4"></div>
                    <h2 className="text-xl font-semibold text-lightblue mb-2">
                        Your results are being processed
                    </h2>
                    <p className="text-lightgray">
                        We are matching your assessment results with available opportunities.
                        This page will update automatically when your matches are ready.
                    </p>
                </div>
            )}

            {isMatched === true && (
            <>
            {selectedTab === "people" && (
                <div className="bg-bluegray p-4 rounded-lg shadow-md">
                    {/*Commented out until Matt changes his mind*/}
                    {/*<h2 className="text-xl font-semibold mb-2 text-lightblue">Top 10 Most Similar to You</h2>*/}
                {/*    {loadingTop ? (*/}
                {/*        <div className="text-center">Loading top matches...</div>*/}
                {/*    ) : (*/}
                {/*        <>*/}
                {/*            {(() => {*/}
                {/*                const similarMatches = topMatches.filter(match => match.similarityScore > 0.75);*/}
                {/*                const closestMatches = topMatches.filter(match => match.similarityScore <= 0.75);*/}

                {/*                return (*/}
                {/*                    <>*/}
                {/*                        <h3 className="text-lg font-semibold text-orange">High Similarity</h3>*/}
                {/*                        <ul>*/}
                {/*                            {similarMatches.map(match => (*/}
                {/*                                <li key={match._id} className="border border-deepblue p-4 my-2 rounded flex justify-between items-center bg-darkblue">*/}
                {/*                                    <div>*/}
                {/*                                        <p className="font-semibold text-lightgray">*/}
                {/*                                            {match.matchedUser.firstName} {match.matchedUser.lastName}*/}
                {/*                                        </p>*/}
                {/*                                        {match.matchedUser.linkedinProfile && (*/}
                {/*                                            <a href={match.matchedUser.linkedinProfile} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">*/}
                {/*                                                LinkedIn Profile*/}
                {/*                                            </a>*/}
                {/*                                        )}*/}
                {/*                                    </div>*/}
                {/*                                    <div>*/}
                {/*                            <span className="font-bold text-orange">*/}
                {/*                                {formatSimilarity(match.similarityScore)}*/}
                {/*                            </span>*/}
                {/*                                    </div>*/}
                {/*                                </li>*/}
                {/*                            ))}*/}
                {/*                        </ul>*/}
                {/*                        {similarMatches.length < 5 && (*/}
                {/*                            <div className="text-center mb-4">*/}
                {/*                                <img*/}
                {/*                                    src="/ShockedCat.png"*/}
                {/*                                    alt="Special"*/}
                {/*                                    className="mx-auto mb-2 w-64 h-auto rounded"*/}
                {/*                                />*/}
                {/*                                <p className="text-xl font-bold text-orange">*/}
                {/*                                    Hmm there aren't many people like you...*/}
                {/*                                </p>*/}
                {/*                                <p className="text-xl font-bold text-orange">*/}
                {/*                                    Your Mom was right, you ARE Special!*/}
                {/*                                </p>*/}
                {/*                            </div>*/}
                {/*                        )}*/}



                {/*                        <h3 className="text-lg font-semibold text-orange">Closest of the Rest</h3>*/}
                {/*                        <ul>*/}
                {/*                            {closestMatches.map(match => (*/}
                {/*                                <li key={match._id} className="border border-deepblue p-4 my-2 rounded flex justify-between items-center bg-darkblue">*/}
                {/*                                    <div>*/}
                {/*                                        <p className="font-semibold text-lightgray">*/}
                {/*                                            {match.matchedUser.firstName} {match.matchedUser.lastName}*/}
                {/*                                        </p>*/}
                {/*                                        <p className="text-gray-400">*/}
                {/*                                            Similarity Score: <span className="font-bold">*/}
                {/*  {(match.similarityScore * 100).toFixed(2)}%*/}
                {/*</span>*/}
                {/*                                        </p>*/}
                {/*                                        {match.matchedUser.linkedinProfile && (*/}
                {/*                                            <a href={match.matchedUser.linkedinProfile} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">*/}
                {/*                                                LinkedIn Profile*/}
                {/*                                            </a>*/}
                {/*                                        )}*/}
                {/*                                    </div>*/}
                {/*                                </li>*/}
                {/*                            ))}*/}
                {/*                        </ul>*/}
                {/*                    </>*/}
                {/*                );*/}
                {/*            })()}*/}
                {/*        </>*/}
                {/*    )}*/}

                    <h2 className="text-xl font-semibold mt-6 mb-2 text-lightblue">Referred People</h2>
                    {loadingReferred ? (
                        <div className="text-center">Loading referred users...</div>
                    ) : (
                        <ul>
                            {referredUsers.map(user => (
                                <li key={user._id} className="border border-deepblue p-4 my-2 rounded flex justify-between items-center bg-darkblue">
                                    <div>
                                        <p className="font-semibold text-lightgray">{user.firstName} {user.lastName}</p>
                                        {typeof user.similarityScore === "number" && (
                                            <p className="text-orange font-semibold">
                                                Similarity: {(user.similarityScore * 100).toFixed(2)}%
                                            </p>
                                        )}
                                        {user.linkedinProfile && (
                                            <a href={user.linkedinProfile} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                                                LinkedIn Profile
                                            </a>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}


            {selectedTab === "jobs" && (
                <div className="bg-bluegray p-4 rounded-lg shadow-md">
                    {loadingJobMatches ? (
                        <div className="bg-bluegray p-8 rounded-lg shadow-md text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange mx-auto mb-4"></div>
                            <p className="text-lightgray">Loading job matches...</p>
                        </div>
                    ) : (
                    <>
                    <h2 className="text-xl font-semibold text-lightblue">Job Matches</h2>
                    <div className="flex items-center mb-4 space-x-2">
                        <label className="text-lightgray">Filter Jobs:</label>
                        <Select
                            options={statusOptions}
                            value={statusOptions.find(opt => opt.value === jobStatusFilter)}
                            onChange={(selected) => setJobStatusFilter(selected.value)}
                            styles={selectStyles}
                            placeholder="Filter Jobs"
                            className="w-64"
                        />
                    </div>

                    {(() => {
                        const filteredMatches = jobMatches.filter(job => job.similarityScore >= 0.77);
                        const sortedMatches = [...filteredMatches].sort((a, b) => b.similarityScore - a.similarityScore);

                        if (sortedMatches.length === 0) {
                            return (
                                <div className="bg-bluegray p-8 rounded-lg shadow-md text-center mt-4">
                                    <div className="text-4xl mb-4">&#127919;</div>
                                    <h2 className="text-xl font-semibold text-lightblue mb-2">No matches yet</h2>
                                    <p className="text-lightgray">
                                        Your profile is active and being compared against open roles.
                                        New matches appear here automatically as employers post jobs.
                                    </p>
                                </div>
                            );
                        }

                        return sortedMatches.map((job, index) => (
                            <div key={job._id} className="border rounded p-3 mb-3">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <p className="font-semibold">{job.title}</p>
                                        <p className="text-xs text-gray-500">Job Status: {job.status}</p>
                                        <p className="text-sm text-orange font-semibold">
                                            Similarity Score: {(job.similarityScore * 100).toFixed(2)}%
                                        </p>
                                        <p className="text-sm text-lightgray">
                                            Rank: You are the <strong>{job.rank ? formatOrdinal(job.rank) : "-"}</strong> ranked candidate for this job.
                                        </p>
                                        <a href={job.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline text-sm">
                                            View Job Post
                                        </a>
                                    </div>
                                    <button
                                        onClick={() => handleApply(job._id, job.applied)}
                                        className={`px-3 py-1 rounded ${
                                            job.applied
                                                ? "bg-red-600 text-white hover:bg-red-700"
                                                : "bg-orange text-white hover:bg-deeporange"
                                        }`}
                                    >
                                        {job.applied ? "Remove Application" : "I Already Applied"}
                                    </button>
                                </div>
                            </div>
                        ));
                    })()}
                    </>
                    )}
                </div>
            )}
            </>
            )}
        </div>
    );
};

export default Matches;
